import React, { useState, useEffect } from 'react';
import { ref, set, remove, onValue, off } from 'firebase/database';
import { managementDb } from '../firebase';
import { toast } from 'react-toastify';
import './ServicesManagement.css';

// Initial hardcoded services (from Services.jsx)
const initialServices = [
  {
    id: 'video-production',
    icon: 'fas fa-video',
    title: 'Video Production',
    description: 'Full-service video production from pre-production planning to final delivery. We handle everything to create stunning visual content.',
    videoUrl: 'https://www.youtube.com/watch?v=n3kNlFMXslo',
    features: [
      { icon: 'fas fa-lightbulb', title: 'Concept Development', description: 'We work with you to develop creative concepts that align with your brand and message.' },
      { icon: 'fas fa-scroll', title: 'Scriptwriting', description: 'Professional scriptwriting services to ensure your message is clear and compelling.' },
      { icon: 'fas fa-video', title: 'Professional Filming', description: 'High-quality filming with professional equipment and experienced crew.' },
      { icon: 'fas fa-microphone', title: 'Audio Recording', description: 'Crystal clear audio recording with professional microphones and sound equipment.' },
      { icon: 'fas fa-film', title: 'Post Production', description: 'Expert editing, color grading, and sound design to polish your video.' }
    ],
    order: 1,
    isActive: true,
    createdAt: Date.now(),
    updatedAt: Date.now()
  },
  {
    id: 'commercial-filming',
    icon: 'fas fa-ad',
    title: 'Commercial Filming',
    description: 'Captivating commercials that showcase your products and services. We create ads that engage your audience and drive results.',
    videoUrl: 'https://www.youtube.com/watch?v=jfKfPfyJRdk',
    features: [
      { icon: 'fas fa-bullseye', title: 'Targeted Messaging', description: 'Ads crafted to speak directly to your target audience.' },
      { icon: 'fas fa-chart-line', title: 'Performance Focused', description: 'Commercials designed to drive conversions and sales.' },
      { icon: 'fas fa-tv', title: 'Multi-Platform', description: 'Optimized for TV, social media, and digital platforms.' },
      { icon: 'fas fa-clock', title: 'Multiple Lengths', description: 'We produce versions in different lengths for various platforms.' }
    ],
    order: 2,
    isActive: false,
    createdAt: Date.now(),
    updatedAt: Date.now()
  },
  {
    id: 'corporate-videos',
    icon: 'fas fa-building',
    title: 'Corporate Videos',
    description: 'Professional corporate videos for training, internal communications, and brand storytelling that reflect your company\'s values.',
    videoUrl: 'https://www.youtube.com/watch?v=5qap5aO4i9A',
    features: [
      { icon: 'fas fa-users', title: 'Employee Training', description: 'Effective training videos for onboarding and continuous learning.' },
      { icon: 'fas fa-handshake', title: 'Brand Storytelling', description: 'Compelling narratives that communicate your company values.' },
      { icon: 'fas fa-bullhorn', title: 'Internal Communications', description: 'Engaging videos for company announcements and updates.' },
      { icon: 'fas fa-chart-pie', title: 'Annual Reports', description: 'Dynamic video versions of your annual reports.' }
    ],
    order: 3,
    isActive: false,
    createdAt: Date.now(),
    updatedAt: Date.now()
  },
  {
    id: 'event-coverage',
    icon: 'fas fa-calendar-alt',
    title: 'Event Coverage',
    description: 'Comprehensive event filming services for conferences, weddings, concerts, and special occasions. Capture every important moment.',
    videoUrl: 'https://www.youtube.com/watch?v=DWcJFNfaw9c',
    features: [
      { icon: 'fas fa-camera-retro', title: 'Multi-Camera Setup', description: 'Multiple angles to capture all the important moments.' },
      { icon: 'fas fa-photo-video', title: 'Live Streaming', description: 'Broadcast your event live to remote audiences.' },
      { icon: 'fas fa-film', title: 'Highlight Reels', description: 'Beautifully edited highlight packages.' },
      { icon: 'fas fa-calendar-check', title: 'Full Event Coverage', description: 'From setup to teardown, we capture it all.' }
    ],
    order: 4,
    isActive: false,
    createdAt: Date.now(),
    updatedAt: Date.now()
  },
  {
    id: 'drone-filming',
    icon: 'fa-solid fa-helicopter',
    title: 'Drone Filming',
    description: 'Breathtaking aerial cinematography that adds a unique perspective to your videos. FAA-certified pilots and high-end equipment.',
    videoUrl: 'https://www.youtube.com/watch?v=TGan48YE9Us',
    features: [
      { icon: 'fas fa-helicopter', title: 'Aerial Perspectives', description: 'Unique vantage points that ground cameras can\'t achieve.' },
      { icon: 'fas fa-certificate', title: 'FAA Certified', description: 'Fully licensed and insured drone operations.' },
      { icon: 'fas fa-video', title: '4K Resolution', description: 'Crystal clear footage in ultra high definition.' },
      { icon: 'fas fa-map-marked-alt', title: 'Location Scouting', description: 'Help finding the perfect locations for your shots.' }
    ],
    order: 5,
    isActive: false,
    createdAt: Date.now(),
    updatedAt: Date.now()
  },
  {
    id: 'post-production',
    icon: 'fas fa-film',
    title: 'Post Production',
    description: 'Expert editing, color grading, sound design, and visual effects to transform raw footage into polished, professional videos.',
    videoUrl: 'https://www.youtube.com/watch?v=21X5lGlDOfg',
    features: [
      { icon: 'fas fa-cut', title: 'Professional Editing', description: 'Seamless editing that tells your story effectively.' },
      { icon: 'fas fa-palette', title: 'Color Grading', description: 'Enhance the mood and tone of your video.' },
      { icon: 'fas fa-wave-square', title: 'Sound Design', description: 'Professional audio mixing and sound effects.' },
      { icon: 'fas fa-magic', title: 'Visual Effects', description: 'Adding motion graphics and special effects.' },
      { icon: 'fas fa-closed-captioning', title: 'Subtitles & Captions', description: 'Make your content accessible to all viewers.' }
    ],
    order: 6,
    isActive: false,
    createdAt: Date.now(),
    updatedAt: Date.now()
  }
];

const ServicesManagement = () => {
  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNew, setIsNew] = useState(false);

  // Initial data push if /services/ is empty
  useEffect(() => {
    const servicesRef = ref(managementDb, 'services');
    onValue(servicesRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        // Push initial data
        const updates = {};
        initialServices.forEach(service => {
          updates[service.id] = service;
        });
        set(servicesRef, updates).then(() => {
          toast.success('Initial services pushed to database!');
          setServices(initialServices);
          setLoading(false);
        });
      } else {
        // Convert to array
        const arr = Object.keys(data).map(id => ({ id, ...data[id] })).sort((a, b) => a.order - b.order);
        setServices(arr);
        setLoading(false);
      }
    }, (err) => {
      toast.error('Failed to load services: ' + err.message);
      setLoading(false);
    });
    // Cleanup
    return () => off(servicesRef);
  }, []);

  // Select service for editing
  const handleSelectService = (id) => {
    setSelectedServiceId(id);
    const service = services.find(s => s.id === id);
    setFormData({ ...service });
    setIsNew(false);
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle feature change
  const handleFeatureChange = (idx, field, value) => {
    setFormData(prev => {
      const features = [...prev.features];
      features[idx][field] = value;
      return { ...prev, features };
    });
  };

  // Add new feature
  const handleAddFeature = () => {
    setFormData(prev => ({ ...prev, features: [...(prev.features || []), { icon: '', title: '', description: '' }] }));
  };

  // Remove feature
  const handleRemoveFeature = (idx) => {
    setFormData(prev => {
      const features = [...prev.features];
      features.splice(idx, 1);
      return { ...prev, features };
    });
  };

  // Save (add or update)
  const handleSave = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Title and description are required');
      return;
    }
    const serviceRef = ref(managementDb, `services/${formData.id}`);
    const data = { ...formData, updatedAt: Date.now() };
    if (isNew) data.createdAt = Date.now();
    await set(serviceRef, data);
    toast.success(isNew ? 'Service added!' : 'Service updated!');
    setSelectedServiceId(null);
    setFormData(null);
    setIsNew(false);
  };

  // Delete service
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await remove(ref(managementDb, `services/${id}`));
    toast.success('Service deleted!');
    setSelectedServiceId(null);
    setFormData(null);
    setIsNew(false);
  };

  // Add new service
  const handleAddNew = () => {
    setFormData({
      id: `service-${Date.now()}`,
      icon: '',
      title: '',
      description: '',
      videoUrl: '',
      features: [],
      order: services.length + 1,
      isActive: false,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
    setSelectedServiceId(null);
    setIsNew(true);
  };

  // Helper function to convert YouTube URL to embed
  const getVideoEmbed = (url) => {
    if (!url) return null;
    
    // Extract video ID from various YouTube URL formats
    const videoId = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\\s]{11})/);
    
    if (videoId) {
      return (
        <iframe
          width="100%"
          height="150"
          src={`https://www.youtube.com/embed/${videoId[1]}`}
          title="Video Preview"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      );
    }
    
    return (
      <div className="invalid-url">
        <i className="fas fa-exclamation-triangle"></i>
        <span>Invalid YouTube URL</span>
      </div>
    );
  };

  if (loading) return <div><div>Loading...</div></div>;

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-cogs me-2"></i>
              Services Management
            </h3>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>Services</span>
            <button className="btn btn-success btn-sm" onClick={handleAddNew}><i className="fas fa-plus"></i> Add</button>
          </div>
          <div className="card-body" style={{ maxHeight: 600, overflowY: 'auto' }}>
            {services.length === 0 && <div>No services found.</div>}
            <div className="row g-3">
              {services.map(service => (
                <div className="col-12 col-md-12" key={service.id}>
                  <div className={`list-group-item service-card-mini ${selectedServiceId === service.id ? 'active' : ''}`} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} onClick={() => handleSelectService(service.id)}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <i className={service.icon} style={{ marginRight: 12, fontSize: 20 }}></i>
                      <span style={{ fontWeight: 600 }}>{service.title}</span>
                    </div>
                    <button className="btn btn-danger btn-sm float-end" style={{ marginLeft: 8 }} onClick={e => { e.stopPropagation(); handleDelete(service.id); }}><i className="fas fa-trash"></i></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-8">
        <div className="card">
          <div className="card-header">{isNew ? 'Add New Service' : formData ? 'Edit Service' : 'Select a Service'}</div>
          <div className="card-body">
            {formData ? (
              <>
                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <input className="form-control" name="title" value={formData.title} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Icon (FontAwesome class)</label>
                  <div className="input-with-preview">
                    <input className="form-control" name="icon" value={formData.icon} onChange={handleChange} />
                    <div className="preview-container">
                      {formData.icon && (
                        <div className="icon-preview">
                          <i className={formData.icon}></i>
                          <span className="icon-name">{formData.icon}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" name="description" value={formData.description} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">YouTube Video URL</label>
                  <div className="input-with-preview">
                    <input className="form-control" name="videoUrl" value={formData.videoUrl} onChange={handleChange} />
                    <div className="preview-container video-container">
                      {formData.videoUrl && (
                        <div className="video-preview">
                          {getVideoEmbed(formData.videoUrl)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Order</label>
                  <input type="number" className="form-control" name="order" value={formData.order} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Active</label>
                  <input type="checkbox" name="isActive" checked={!!formData.isActive} onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Features</label>
                  {formData.features && formData.features.map((feature, idx) => (
                    <div key={idx} className="mb-2 p-2 border rounded">
                      <div className="input-with-preview">
                        <input className="form-control mb-1" placeholder="Icon (FontAwesome)" value={feature.icon} onChange={e => handleFeatureChange(idx, 'icon', e.target.value)} />
                        <div className="preview-container">
                          {feature.icon && (
                            <div className="icon-preview small">
                              <i className={feature.icon}></i>
                              <span className="icon-name small">{feature.icon}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <input className="form-control mb-1" placeholder="Title" value={feature.title} onChange={e => handleFeatureChange(idx, 'title', e.target.value)} />
                      <input className="form-control mb-1" placeholder="Description" value={feature.description} onChange={e => handleFeatureChange(idx, 'description', e.target.value)} />
                      <button className="btn btn-danger btn-sm" onClick={() => handleRemoveFeature(idx)}>Remove</button>
                    </div>
                  ))}
                  <button className="btn btn-primary btn-sm mt-2" onClick={handleAddFeature}><i className="fas fa-plus"></i> Add Feature</button>
                </div>
                <button className="btn btn-success me-2" onClick={handleSave}>{isNew ? 'Add' : 'Update'}</button>
                <button className="btn btn-secondary" onClick={() => { setFormData(null); setSelectedServiceId(null); setIsNew(false); }}>Cancel</button>
              </>
            ) : (
              <div>Select a service to edit or click Add to create a new one.</div>
            )}
          </div>
        </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesManagement;